/**
 * Facts-form controls — the closed set of input boxes Vee can ask for.
 *
 * `request_facts` fields carry a `type` the MODEL picks ("date", "amount",
 * "select"…), and this module maps each one to the brand control and to the
 * string that belongs in a statutory document. The point of typing the field at
 * the tool call rather than guessing it here: a free-text box lets "sometime
 * next week" into a filing; a calendar picker cannot.
 *
 * Three rules keep it safe:
 *   • CLOSED SET — the backend drops any type outside FIELD_TYPES, and an
 *     unknown/missing one degrades to a plain text box. A hallucinated control
 *     name can never reach the renderer.
 *   • NO TYPE STILL WORKS — sessions saved before typed fields, and the
 *     deterministic placeholder-fill form (which builds its own fields from the
 *     linter's tokens), carry no `type`; the key/label regexes below infer
 *     date/time exactly as they did before.
 *   • THE PICKER NEVER INVENTS — a control only shapes what the user enters. No
 *     control fills a value on its own (see placeholderPrefill for what may be
 *     seeded from the company record and what deliberately may not).
 *
 * KEEP `FIELD_TYPES` IN SYNC with the `type` enum in
 * backend/new-ui-generation/chat/tools/drafting.js (request_facts).
 */
import { useState } from 'react';
import TimePicker from '../components/common/TimePicker';
import { DateField, Select, inputClass, textareaClass } from '../components/common/FormControls';

export const FIELD_TYPES = ['text', 'textarea', 'date', 'time', 'number', 'amount', 'select', 'person', 'company'];

// Sentinel for the person picker's escape hatch. Not a plausible director name,
// so it can never collide with a real option value.
const OTHER_OPTION = '__other__';

/* ── Type resolution ────────────────────────────────────────────────────── */

// Legacy inference, unchanged from when the widget was guessed here. Still the
// only signal for the placeholder-fill form and for restored older sessions.
const looksLikeDate = (f) => /(^|_)date(_|$)|\bdate\b/i.test(`${f.key || ''} ${f.label || ''}`) && !/dates?\s+range/i.test(f.label || '');
const looksLikeTime = (f) => /(^|_)time(_|$)|\btime\b/i.test(`${f.key || ''} ${f.label || ''}`);

/** Which control a field renders as. Explicit `type` always wins. */
export function resolveFieldType(field = {}) {
  const declared = String(field.type || '').trim().toLowerCase();
  if (FIELD_TYPES.includes(declared)) return declared;
  // Untyped field carrying options = the director picker attached client-side
  // by withPrefilledValues / attachPersonOptions.
  if (Array.isArray(field.options) && field.options.length) return 'person';
  if (looksLikeDate(field)) return 'date';
  if (looksLikeTime(field)) return 'time';
  return 'text';
}

/* ── Value → document string ────────────────────────────────────────────── */

// A yyyy-mm-dd picker value → "5 August 2026" (what belongs in a document);
// non-ISO values (prefills like "5 August 2026") pass through unchanged.
// "Day, Date" wants "Wednesday, 5 August 2026"; a plain "Date" does not. The
// word boundaries matter — "birthday" and "holiday" are not weekday requests.
const wantsWeekday = (field = {}) => /\bday\b/i.test(`${field.key || ''} ${field.label || ''}`);

const displayDate = (v, withWeekday = false) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v || '').trim());
  if (!m) return v;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString('en-IN', {
    ...(withWeekday ? { weekday: 'long' } : {}),
    day: 'numeric', month: 'long', year: 'numeric',
  });
};

// An HH:MM picker value → "11:00 A.M."; anything else passes through.
const displayTime = (v) => {
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(String(v || '').trim());
  if (!m) return v;
  const h = Number(m[1]);
  const suffix = h >= 12 ? 'P.M.' : 'A.M.';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m[2]} ${suffix}`;
};

// "1500000" → "₹15,00,000" (Indian grouping: last 3, then pairs). The amount
// box only accepts digits and separators, so there is no "15 lakh" to misread —
// anything that still fails to parse is returned VERBATIM rather than mangled.
const displayAmount = (v) => {
  const raw = String(v ?? '').trim();
  if (!raw) return v;
  const n = Number(raw.replace(/,/g, ''));
  if (!Number.isFinite(n) || n <= 0) return v;
  const [whole, frac] = String(n).split('.');
  const last3 = whole.slice(-3);
  const rest = whole.slice(0, -3);
  const grouped = rest ? `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}` : last3;
  return `₹${grouped}${frac ? `.${frac}` : ''}`;
};

/** The form's raw picker value → the string the document should print. */
export function toDocumentValue(field, value) {
  switch (resolveFieldType(field)) {
    case 'date': return displayDate(value, wantsWeekday(field));
    case 'time': return displayTime(value);
    case 'amount': return displayAmount(value);
    default: return value;
  }
}

/* ── Controls ───────────────────────────────────────────────────────────── */

const digitsOnly = (s) => String(s || '').replace(/\D+/g, '');
const amountChars = (s) => String(s || '').replace(/[^\d.,]/g, '');

const normalizeOptions = (options = []) => options
  .map((o) => (typeof o === 'string' ? { value: o, label: o } : o))
  .filter((o) => o && o.value);

/**
 * Directors picker with a type-it-in escape. The escape is not optional
 * chrome: in an APPOINTMENT resolution the person named is by definition not
 * yet on the board, so the list cannot contain them.
 */
function PersonField({ field, value, onChange, disabled, filledStyle }) {
  const [manual, setManual] = useState(false);
  const options = normalizeOptions(field.options);

  if (manual || !options.length) {
    return (
      <>
        <input
          className={inputClass}
          style={filledStyle}
          value={value || ''}
          placeholder={field.hint || 'Type the name'}
          onChange={(e) => onChange({ [field.key]: e.target.value })}
          disabled={disabled}
        />
        {manual && (
          <button
            type="button"
            className="mt-1 text-[10.5px] font-semibold text-[#7a7060] hover:text-[#145f5f]"
            onClick={() => { setManual(false); onChange({ [field.key]: '' }); }}
          >
            ← Back to the director list
          </button>
        )}
      </>
    );
  }

  return (
    <Select
      value={value || ''}
      disabled={disabled}
      placeholder="Select a director…"
      options={[...options, { value: OTHER_OPTION, label: 'Someone else — type it in' }]}
      onChange={(picked) => {
        if (picked === OTHER_OPTION) {
          setManual(true);
          onChange({ [field.key]: '' });
          return;
        }
        // Picking a director fills their DIN (or name) too, so the pair can
        // never drift apart. Every patched key counts as touched upstream — a
        // late prefill must not undo the user's choice.
        const opt = options.find((o) => o.value === picked);
        onChange({ [field.key]: picked, ...(opt?.fills || {}) });
      }}
    />
  );
}

/**
 * "Which company is this document for?" — a picker over the user's OWN
 * portfolio, attached client-side by attachCompanyOptions.
 *
 * No type-it-in escape, deliberately, and that is the opposite of PersonField:
 * a director can be someone not yet on the board, but a company the user does
 * not have cannot be the subject of a document drafted from their records. A
 * free-text box here would take a name we cannot resolve to a CIN, and the
 * draft would silently fall back to [BRACKETED] placeholders — the failure this
 * picker exists to prevent. If their company is missing they need to add it,
 * not type it.
 *
 * Picking also fills the sibling CIN key (see attachCompanyOptions) so the
 * answer carries the identifier the drafting tools need, not just a name.
 */
function CompanyField({ field, value, onChange, disabled }) {
  const options = normalizeOptions(field.options);
  if (!options.length) {
    return (
      <p className="text-[11.5px] text-[#7a7060] italic py-1">
        No companies on this account yet — add one first, then ask again.
      </p>
    );
  }
  return (
    <Select
      value={value || ''}
      disabled={disabled}
      placeholder="Select a company…"
      options={options}
      onChange={(picked) => {
        const opt = options.find((o) => o.value === picked);
        onChange({ [field.key]: picked, ...(opt?.fills || {}) });
      }}
    />
  );
}

/**
 * One facts-form row's control.
 *
 * @param {object}   field     { key, label, hint?, type?, options? }
 * @param {string}   value     current value for this field
 * @param {Function} onChange  called with a PATCH object — a person pick sets
 *                             its sibling DIN in the same call
 */
export function FactsField({ field, value, onChange, disabled }) {
  const type = resolveFieldType(field);
  const text = String(value ?? '');
  // Filled fields turn teal — the user can see at a glance what is still open.
  const filledStyle = text.trim() ? { borderColor: '#145f5f' } : undefined;
  const set = (v) => onChange({ [field.key]: v });

  if (type === 'person') {
    return <PersonField field={field} value={text} onChange={onChange} disabled={disabled} filledStyle={filledStyle} />;
  }

  if (type === 'company') {
    return <CompanyField field={field} value={text} onChange={onChange} disabled={disabled} />;
  }

  if (type === 'select') {
    return (
      <Select
        value={text}
        onChange={set}
        disabled={disabled}
        options={normalizeOptions(field.options)}
        placeholder={field.hint || '— Select —'}
      />
    );
  }

  // The pickers only render while the value is EMPTY or already in their own
  // machine format. A human-format prefill ("5 August 2026", resolved from the
  // company record) keeps a text box so it stays readable and editable.
  if (type === 'time' && /^(([01]?\d|2[0-3]):[0-5]\d)?$/.test(text.trim())) {
    return <TimePicker value={text} onChange={set} disabled={disabled} />;
  }

  if (type === 'date' && /^(\d{4}-\d{2}-\d{2})?$/.test(text.trim())) {
    return <DateField value={text} onChange={set} disabled={disabled} placeholder={field.hint || 'Select date'} />;
  }

  if (type === 'textarea') {
    return (
      <textarea
        rows={3}
        className={`${textareaClass} min-h-[76px]`}
        style={filledStyle}
        value={text}
        placeholder={field.hint || ''}
        onChange={(e) => set(e.target.value)}
        disabled={disabled}
      />
    );
  }

  if (type === 'amount') {
    return (
      <div className="relative">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[13px] text-[#7a7060] pointer-events-none">₹</span>
        <input
          className={`${inputClass} pl-7`}
          style={filledStyle}
          inputMode="decimal"
          value={text}
          placeholder={field.hint || '0'}
          onChange={(e) => set(amountChars(e.target.value))}
          disabled={disabled}
        />
      </div>
    );
  }

  return (
    <input
      className={inputClass}
      style={filledStyle}
      inputMode={type === 'number' ? 'numeric' : undefined}
      value={text}
      placeholder={field.hint || ''}
      onChange={(e) => set(type === 'number' ? digitsOnly(e.target.value) : e.target.value)}
      disabled={disabled}
    />
  );
}
